// Small building blocks shared by the AI Forms pages, and their styles
// (scoped under .aif so nothing leaks into the rest of the admin).

import React, { useId } from 'react';
import styled from 'styled-components';
import { I } from '../../../common/v5/config';
import { setText, textIn, statusOf } from './definition';

const blue = "#0c87f7" // $centionBlue
	, blueDark = "#0a6fcb"
	, red = "#d0263e"
	, green = "#16794a"
	, amber = "#946200"
	, line = "#dde2e8"
	, muted = "#6b7480"
	, ink = "#1f2328"
	;

export const Root = styled.div`
	color: ${ink};
	font-size: 13px;
	height: 100%;
	&, & * { box-sizing: border-box; }
	.aif-page { padding: 16px 20px; max-width: 1200px; }
	.aif-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
	.aif-head h2 { margin: 0; font-size: 18px; font-weight: 600; }
	.aif-lead { margin: 4px 0 0; color: ${muted}; max-width: 680px; }
	.aif-muted { color: ${muted}; }
	.aif-small { font-size: 12px; }
	.aif-error { color: ${red}; font-size: 12px; margin-top: 4px; }
	.aif-btn { display: inline-flex; align-items: center; gap: 6px; border: 1px solid ${line}; background: #fff; color: ${ink};
		border-radius: 6px; padding: 6px 12px; font-size: 13px; cursor: pointer; white-space: nowrap; text-decoration: none; }
	.aif-btn:hover { background: #f4f6f8; }
	.aif-btn:disabled { opacity: .55; cursor: default; }
	.aif-btn.primary { background: ${blue}; border-color: ${blue}; color: #fff; font-weight: 500; }
	.aif-btn.primary:hover:not(:disabled) { background: ${blueDark}; }
	.aif-btn.danger:hover { background: #fdecef; color: ${red}; border-color: #f3b8c1; }
	.aif-link { background: none; border: 0; padding: 0; color: ${blue}; cursor: pointer; font-size: 12px; font-weight: 500; }
	.aif-link:hover { text-decoration: underline; }
	.aif-icon { background: none; border: 0; padding: 4px 6px; border-radius: 4px; color: ${muted}; cursor: pointer; }
	.aif-icon:hover:not(:disabled) { background: #eef1f4; color: ${ink}; }
	.aif-icon:disabled { opacity: .3; cursor: default; }
	.aif-input, .aif-page select, .aif-editor select, .aif-editor textarea, .aif-editor input[type=text],
	.aif-editor input[type=number], .aif-dialog input[type=text], .aif-dialog select {
		width: 100%; border: 1px solid ${line}; border-radius: 6px; padding: 6px 9px; font-size: 13px; background: #fff; color: ${ink};
		font-family: inherit; }
	.aif-input:focus, .aif-editor select:focus, .aif-editor textarea:focus, .aif-editor input:focus { outline: none; border-color: ${blue}; box-shadow: 0 0 0 1px ${blue}; }
	.aif-mono { font-family: Menlo, Consolas, monospace; font-size: 12px; }
	.aif-table { width: 100%; margin-top: 16px; border: 1px solid ${line}; border-radius: 8px; border-collapse: separate; border-spacing: 0; background: #fff; overflow: hidden; }
	.aif-table th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: ${muted}; font-weight: 600;
		padding: 9px 14px; background: #f6f8fa; border-bottom: 1px solid ${line}; }
	.aif-table td { padding: 10px 14px; border-bottom: 1px solid #eef1f4; vertical-align: top; }
	.aif-table tr:last-child td { border-bottom: 0; }
	.aif-table tbody tr { cursor: pointer; }
	.aif-table tbody tr:hover td { background: #f8fafc; }
	.aif-name { font-weight: 600; color: ${ink}; }
	.aif-badge { display: inline-block; border-radius: 999px; padding: 1px 8px; font-size: 11px; font-weight: 600; }
	.aif-badge.draft { background: #eef1f4; color: #4a525c; }
	.aif-badge.live { background: #e3f5ec; color: ${green}; }
	.aif-badge.changed { background: #fdf3dc; color: ${amber}; }
	.aif-empty { margin-top: 32px; padding: 48px 24px; text-align: center; border: 1px dashed #c8cfd8; border-radius: 10px; }
	.aif-empty h3 { margin: 8px 0 4px; font-size: 15px; }
	.aif-overlay { position: fixed; inset: 0; z-index: 2000; background: rgba(15,23,42,.35); display: flex; align-items: center; justify-content: center; padding: 16px; }
	.aif-dialog { width: 100%; max-width: 440px; background: #fff; border-radius: 10px; padding: 22px; box-shadow: 0 12px 40px rgba(0,0,0,.2); }
	.aif-dialog h3 { margin: 0; font-size: 16px; }
	.aif-dialog label.aif-lbl { margin-top: 14px; }
	.aif-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }

	/* editor */
	.aif-editor { display: flex; flex-direction: column; height: calc(100vh - 56px); min-height: 600px; background: #f6f8fa; }
	.aif-bar { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; padding: 8px 14px; background: #fff; border-bottom: 1px solid ${line}; }
	.aif-bar .aif-title { flex: 1 1 200px; max-width: 360px; font-size: 16px; font-weight: 600; border-color: transparent; }
	.aif-bar .aif-title:hover { border-color: ${line}; }
	.aif-bar .aif-right { margin-left: auto; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
	.aif-save { font-size: 12px; color: ${muted}; display: inline-flex; gap: 5px; align-items: center; }
	.aif-save.error { color: ${red}; }
	.aif-body { display: flex; flex: 1; min-height: 0; }
	.aif-left { width: 54%; min-width: 420px; display: flex; flex-direction: column; min-height: 0; border-right: 1px solid ${line}; }
	.aif-tabs { display: flex; align-items: center; justify-content: space-between; gap: 10px; background: #fff; border-bottom: 1px solid ${line}; padding: 0 12px; }
	.aif-tabs nav { display: flex; gap: 2px; overflow-x: auto; }
	.aif-tab { background: none; border: 0; border-bottom: 2px solid transparent; padding: 10px 12px; font-size: 13px; color: #4a525c; cursor: pointer; white-space: nowrap; display: inline-flex; gap: 6px; align-items: center; }
	.aif-tab:hover { color: ${ink}; }
	.aif-tab.active { border-bottom-color: ${blue}; color: ${blue}; font-weight: 600; }
	.aif-count { background: #fbe2e6; color: ${red}; border-radius: 999px; padding: 0 6px; font-size: 10px; font-weight: 700; }
	.aif-lang { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: ${muted}; white-space: nowrap; }
	.aif-lang select { width: auto; padding: 3px 6px; font-size: 12px; }
	.aif-scroll { flex: 1; min-height: 0; overflow-y: auto; padding: 14px; }
	.aif-stack > * + * { margin-top: 14px; }
	.aif-alert { border: 1px solid #f3b8c1; background: #fdf0f2; color: #8a1426; border-radius: 8px; padding: 10px 12px; }
	.aif-alert ul { margin: 4px 0 0; padding: 0; list-style: none; }
	.aif-alert li { display: flex; justify-content: space-between; gap: 12px; margin-top: 3px; }
	.aif-note { border: 1px solid #f1d89a; background: #fdf8ea; color: #6b4a00; border-radius: 8px; padding: 8px 12px; }
	.aif-section { background: #fff; border: 1px solid ${line}; border-radius: 10px; padding: 14px; }
	.aif-section-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; }
	.aif-section-head h4 { margin: 0; font-size: 14px; font-weight: 600; }
	.aif-section-head p { margin: 2px 0 0; font-size: 12px; color: ${muted}; }
	.aif-section-body > * + * { margin-top: 12px; }
	.aif-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
	.aif-grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
	.aif-grid4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
	.aif-builtin { display: grid; grid-template-columns: 1fr 1fr auto; gap: 12px; align-items: end; }
	.aif-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 22px; }
	.aif-spread { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; }
	label.aif-lbl { display: block; margin: 0 0 4px; font-size: 12px; font-weight: 500; color: #4a525c; }
	.aif-hint { margin-top: 4px; font-size: 12px; color: #8a929c; }
	.aif-check { display: inline-flex; align-items: center; gap: 6px; font-weight: normal; margin: 0; cursor: pointer; }
	.aif-check input { margin: 0; }
	.aif-check.disabled { color: #9aa1aa; cursor: default; }
	.aif-switch { display: flex; align-items: flex-start; gap: 10px; }
	.aif-switch button { position: relative; flex-shrink: 0; width: 36px; height: 20px; margin-top: 1px; border-radius: 999px; border: 0; background: #c3c9d1; cursor: pointer; padding: 0; }
	.aif-switch button.on { background: ${blue}; }
	.aif-switch button span { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.25); transition: left .15s; }
	.aif-switch button.on span { left: 18px; }
	.aif-switch label { margin: 0; cursor: pointer; font-weight: 500; }
	.aif-switch label small { display: block; font-weight: normal; font-size: 12px; color: ${muted}; }
	.aif-key { width: 160px !important; }
	.aif-add { width: 100%; border: 1px dashed #c3c9d1; border-radius: 10px; padding: 11px; background: none; color: #4a525c; cursor: pointer; font-size: 13px; }
	.aif-add:hover:not(:disabled) { border-color: ${blue}; color: ${blue}; }
	.aif-add:disabled { opacity: .5; }
	.aif-addfield { display: flex; align-items: center; gap: 8px; border: 1px dashed #c3c9d1; border-radius: 10px; padding: 10px; }
	.aif-addfield select { width: 180px !important; }
	.aif-colour { display: flex; align-items: center; gap: 8px; }
	.aif-colour input[type=color] { width: 40px; height: 30px; padding: 0 2px; border: 1px solid ${line}; border-radius: 4px; cursor: pointer; flex-shrink: 0; background: #fff; }
	.aif-colour .aif-check { font-size: 12px; flex-shrink: 0; }
	.aif-preset { text-align: left; border: 1px solid ${line}; background: #fff; border-radius: 8px; padding: 8px 10px; cursor: pointer; }
	.aif-preset:hover { border-color: ${blue}; background: #f0f7ff; }
	.aif-preset b { display: block; font-size: 13px; font-weight: 600; }
	.aif-preset span { display: block; font-size: 12px; color: ${muted}; }
	.aif-code { margin: 0; white-space: pre-wrap; word-break: break-all; background: #1f2328; color: #f0f3f6; border-radius: 8px; padding: 10px 12px; font-family: Menlo, Consolas, monospace; font-size: 12px; }
	.aif-copyhead { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
	.aif-stats { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; text-align: center; }
	.aif-right-pane { flex: 1; min-width: 0; display: flex; flex-direction: column; background: #eef1f4; }
	.aif-previewbar { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 6px 14px; background: rgba(255,255,255,.7); border-bottom: 1px solid ${line}; font-size: 12px; color: ${muted}; }
	.aif-previewbar .aif-icon.on { background: #fff; color: ${blue}; box-shadow: 0 1px 2px rgba(0,0,0,.08); }
	.aif-previewbox { flex: 1; min-height: 0; overflow: auto; display: flex; justify-content: center; padding: 14px; }
	.aif-previewbox iframe { width: 100%; height: 100%; min-height: 640px; border: 1px solid ${line}; border-radius: 10px; background: #fff; }
	.aif-previewbox iframe.mobile { width: 390px; flex-shrink: 0; }
	.aif-center { display: flex; justify-content: center; padding: 40px; color: #9aa1aa; }
	details summary { cursor: pointer; color: ${blue}; font-weight: 500; }
	code { background: #eef1f4; padding: 0 4px; border-radius: 3px; font-size: 12px; }

	@media (max-width: 1100px) {
		.aif-body { flex-direction: column; }
		.aif-left { width: 100%; min-width: 0; border-right: 0; }
		.aif-right-pane { min-height: 560px; }
	}
	@media (max-width: 700px) {
		.aif-grid2, .aif-grid3, .aif-builtin { grid-template-columns: 1fr; }
		.aif-grid4 { grid-template-columns: 1fr 1fr; }
	}
`;

export function Spinner() {
	return <i className="fa fa-circle-o-notch fa-spin" aria-hidden="true" />;
}

export function Section({ title, hint, action, children }) {
	return (
		<section className="aif-section">
			<div className="aif-section-head">
				<div>
					<h4>{title}</h4>
					{hint && <p>{hint}</p>}
				</div>
				{action}
			</div>
			<div className="aif-section-body">{children}</div>
		</section>
	);
}

// A labelled control; `children` is a function of the input id.
export function Field({ label, hint, error, children }) {
	const id = useId();
	return (
		<div>
			<label htmlFor={id} className="aif-lbl">{label}</label>
			{children(id)}
			{hint && !error && <div className="aif-hint">{hint}</div>}
			{error && <div className="aif-error" role="alert">{error}</div>}
		</div>
	);
}

// A per-language text: edits `lang`; shows the main language's text as placeholder.
export function LangText({ label, value, lang, mainLang, onChange, multiline, hint, error }) {
	const current = (value && value[lang]) || "";
	const fallback = lang !== mainLang ? textIn(value, mainLang, mainLang) : "";
	const fullLabel = label + (lang !== mainLang ? " (" + lang.toUpperCase() + ")" : "");
	return (
		<Field label={fullLabel} hint={hint} error={error}>
			{id => multiline ? (
				<textarea id={id} rows={3} value={current} placeholder={fallback}
					onChange={e => onChange(setText(value, lang, e.target.value))} />
			) : (
				<input id={id} type="text" value={current} placeholder={fallback}
					onChange={e => onChange(setText(value, lang, e.target.value))} />
			)}
		</Field>
	);
}

export function Switch({ checked, onChange, label, description }) {
	const id = useId();
	return (
		<div className="aif-switch">
			<button id={id} type="button" role="switch" aria-checked={!!checked}
				className={checked ? "on" : ""} onClick={() => onChange(!checked)}>
				<span />
			</button>
			<label htmlFor={id}>
				{label}
				{description && <small>{description}</small>}
			</label>
		</div>
	);
}

export function Check({ checked, onChange, label, disabled }) {
	return (
		<label className={"aif-check" + (disabled ? " disabled" : "")}>
			<input type="checkbox" checked={!!checked} disabled={disabled}
				onChange={e => onChange(e.target.checked)} />
			{label}
		</label>
	);
}

export function IconButton({ label, icon, onClick, disabled }) {
	return (
		<button type="button" className="aif-icon" aria-label={label} title={label}
			onClick={onClick} disabled={disabled}>
			<i className={"fa " + icon} aria-hidden="true" />
		</button>
	);
}

// Up / down / remove for an item in a list.
export function MoveRemove({ index, count, onMove, onRemove, removeLabel, canRemove }) {
	return (
		<div style={{ display: "flex", gap: 2 }}>
			<IconButton label={I("Move up")} icon="fa-arrow-up" disabled={index === 0} onClick={() => onMove(-1)} />
			<IconButton label={I("Move down")} icon="fa-arrow-down" disabled={index === count - 1} onClick={() => onMove(1)} />
			<IconButton label={removeLabel} icon="fa-trash-o" disabled={canRemove === false} onClick={onRemove} />
		</div>
	);
}

const STATUS_LABEL = {
	draft: I("Draft"),
	live: I("Live"),
	changed: I("Live · unpublished changes"),
};

export function StatusBadge({ form }) {
	const status = statusOf(form);
	return <span className={"aif-badge " + status}>{STATUS_LABEL[status]}</span>;
}
